import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { from, Observable, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiService } from '../../api.service';
import { SelectionStateService } from '../../selection-state.service';
import { USER_ROLE } from 'src/app/types/user-role.types';
import { EventKpiSection } from 'src/app/models/core/kpis.model';

@Injectable({ providedIn: 'root' })
export class GlobalReportApiService {
  private baseUrl = `${environment.api_server}/api/sales-manager/events`;

  constructor(
    private _http: HttpClient,
    private _api: ApiService,
    private _selectionState: SelectionStateService,
  ) { }

  /**
   * Agrega `role=<rol seleccionado>` a los params de un request a
   * `/sales-manager/*`. El back (`validateRequestedRole`) usa este valor
   * para acotar el scope (jefe de ventas ve solo su local, no toda la
   * productora). Sin un rol seleccionado real (`unknown`) no se manda el
   * param: mandar un valor inválido haría que el back devuelva 422.
   */
  private withRole(params: HttpParams): HttpParams {
    const role = this._selectionState.getSelectedRoleValue();

    if (!role || role === USER_ROLE.UNKNOWN) {
      return params;
    }

    return params.set('role', role);
  }

  getKpis(eventId: number | string, section: EventKpiSection): Observable<any> {
    const url = `${this.baseUrl}/${eventId}/kpis`;
    const params = this.withRole(new HttpParams().set('section', section));

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this._http.get(url, { headers, params })
      )
    );
  }

  getSalesTypeGraph(eventId: number | string, range: string): Observable<any> {
    const url = `${this.baseUrl}/${eventId}/sales-type-table`;
    const params = this.withRole(new HttpParams().set('range', range));

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this._http.get(url, { headers, params })
      )
    );
  }

  getDemographics(eventId: number | string): Observable<any> {
    const url = `${this.baseUrl}/${eventId}/demographics`;
    const params = this.withRole(new HttpParams());

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this._http.get(url, { headers, params })
      )
    );
  }

  getSellers(
    eventId: number | string,
    sort: string = 'most_sales',
    search: string = '',
    page: number = 1,
    perPage: number = 10,
  ): Observable<any> {
    const url = `${this.baseUrl}/${eventId}/sellers`;
    let params = new HttpParams()
      .set('sort', sort)
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    if (search) {
      params = params.set('search', search);
    }

    params = this.withRole(params);

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this._http.get(url, { headers, params })
      )
    );
  }

  getSellerProducts(
    eventId: number | string,
    sellerId: number,
    page: number = 1,
    perPage: number = 10,
  ): Observable<any> {
    const url = `${this.baseUrl}/${eventId}/sellers/${sellerId}/products`;
    const params = this.withRole(
      new HttpParams()
        .set('page', page.toString())
        .set('per_page', perPage.toString())
    );

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this._http.get(url, { headers, params })
      )
    );
  }

  getSellerCategories(
    eventId: number | string,
    sellerId: number,
    page: number = 1,
    perPage: number = 10,
  ): Observable<any> {
    const url = `${this.baseUrl}/${eventId}/sellers/${sellerId}/categories`;
    const params = this.withRole(
      new HttpParams()
        .set('page', page.toString())
        .set('per_page', perPage.toString())
    );

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this._http.get(url, { headers, params })
      )
    );
  }

  getRankedProducts(
    eventId: number | string,
    sort: string = 'most_sold',
    page: number = 1,
    perPage: number = 10,
  ): Observable<any> {
    const url = `${this.baseUrl}/${eventId}/ranked-products`;
    const params = this.withRole(
      new HttpParams()
        .set('sort', sort)
        .set('page', page.toString())
        .set('per_page', perPage.toString())
    );

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this._http.get(url, { headers, params })
      )
    );
  }

  // Categorías por evento son pocas: se traen todas de una (per_page grande,
  // sin paginar). Mismo shape de item que ranked-product-types.
  getRankedCategories(
    eventId: number | string,
    sort: string = 'most_sold',
    perPage: number = 100,
  ): Observable<any> {
    const url = `${this.baseUrl}/${eventId}/ranked-categories`;
    const params = this.withRole(
      new HttpParams()
        .set('sort', sort)
        .set('per_page', perPage.toString())
    );

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this._http.get(url, { headers, params })
      )
    );
  }

  getRankedProductTypes(
    eventId: number | string,
    sort: string = 'most_sold',
    perPage: number = 100,
  ): Observable<any> {
    const url = `${this.baseUrl}/${eventId}/ranked-product-types`;
    const params = this.withRole(
      new HttpParams()
        .set('sort', sort)
        .set('per_page', perPage.toString())
    );

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this._http.get(url, { headers, params })
      )
    );
  }

  // Locales de un evento son pocos: se traen todos de una (per_page grande, sin
  // paginar) para alimentar la sub-tab "Locales" de Grupo de ventas sin el
  // payload pesado de v2/reports.
  getSellersByLocation(
    eventId: number | string,
    sort: string = 'most_sales',
    perPage: number = 200,
  ): Observable<any> {
    const url = `${this.baseUrl}/${eventId}/sellers-by-location`;
    const params = this.withRole(
      new HttpParams()
        .set('sort', sort)
        .set('per_page', perPage.toString())
    );

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this._http.get(url, { headers, params })
      )
    );
  }

  // Productos por local: locales/productos de un evento son pocos, se traen
  // todos de una (per_page grande, sin paginar) para la sub-tab "Productos" de
  // Grupo de ventas sin el payload pesado de v2/reports. Espeja sellers-by-location.
  getProductsByLocation(
    eventId: number | string,
    sort: string = 'most_sales',
    perPage: number = 200,
  ): Observable<any> {
    const url = `${this.baseUrl}/${eventId}/products-by-location`;
    const params = this.withRole(
      new HttpParams()
        .set('sort', sort)
        .set('per_page', perPage.toString())
    );

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this._http.get(url, { headers, params })
      )
    );
  }

  // Categorías por local: mismo patrón que products-by-location.
  getCategoriesByLocation(
    eventId: number | string,
    sort: string = 'most_sales',
    perPage: number = 200,
  ): Observable<any> {
    const url = `${this.baseUrl}/${eventId}/categories-by-location`;
    const params = this.withRole(
      new HttpParams()
        .set('sort', sort)
        .set('per_page', perPage.toString())
    );

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this._http.get(url, { headers, params })
      )
    );
  }

  // Ventas (lista plana de tickets): paginada con scroll infinito. Son miles de
  // tickets, así que NO se traen todos: page/per_page + búsqueda opcional.
  getSales(
    eventId: number | string,
    page: number = 1,
    perPage: number = 30,
    search: string = '',
  ): Observable<any> {
    const url = `${this.baseUrl}/${eventId}/sales`;
    let params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    if (search) {
      params = params.set('search', search);
    }

    params = this.withRole(params);

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this._http.get(url, { headers, params })
      )
    );
  }
}
